import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { BucketBars, OTHER_COLOR, SERIES_COLORS, ShareDonut, SignupsByDay } from './charts';
import { useCachedState, useRefreshVersion } from './store';
import { Badge, Empty, ErrorState, Metric, Select, ago, date, dayOnly, shortNumber, useMe } from './ui';

const PAGE_SIZE = 25;
const SORTS = [
  ['active', 'Most active (30 days)'], ['newest', 'Newest first'], ['renders', 'Most renders'], ['downloads', 'Most downloads'], ['voice', 'Most voiceover use'],
];
const PLAN_FILTERS = [['', 'Any plan'], ['paid', 'Paying now'], ['pro', 'Pro'], ['studio', 'Studio'], ['lapsed', 'Plan ended'], ['free', 'Never paid']];
const PLAN_NAMES = { pro: 'Pro', studio: 'Studio' };
const voiceMinutes = (seconds) => { const s = Number(seconds ?? 0); return s < 60 ? `${Math.round(s)} s` : `${(s / 60).toFixed(1)} min`; };

/**
 * What the customer is on right now, read from current_plan() on the server —
 * the same function that decides their limits, so this badge cannot say Pro
 * while the account is being charged as Free.
 */
function PlanCell({ u }) {
  if (u.plan_id) {
    const renews = u.plan_paid_through && u.plan_paid_through !== u.plan_period_end;
    return <><Badge tone={u.plan_id === 'studio' ? 'info' : 'good'}>{PLAN_NAMES[u.plan_id] ?? u.plan_id}</Badge>
      <small className="plain">{renews ? `paid through ${dayOnly(u.plan_paid_through)}` : `until ${dayOnly(u.plan_period_end)}`}</small></>;
  }
  if (u.last_plan_id) {
    return <><Badge tone="warn">Ended</Badge><small className="plain">{PLAN_NAMES[u.last_plan_id] ?? u.last_plan_id} · {dayOnly(u.last_plan_ended_at)}</small></>;
  }
  return <Badge>Free</Badge>;
}

const regionNames = typeof Intl !== 'undefined' && Intl.DisplayNames ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;
export const countryName = (code) => { if (!code) return null; try { return regionNames?.of(code) ?? code; } catch { return code; } };
/** Regional indicator symbols: "IN" → 🇮🇳. Two code points, no lookup table. */
export const flagOf = (code) => (code && /^[A-Z]{2}$/.test(code) ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '');

/**
 * A country flag as an image, not an emoji. Windows ships no flag glyphs at
 * all — Segoe UI Emoji renders a flag as its two letters — so every operator
 * on a Windows machine saw "IN" where the flag should be. The emoji is kept
 * as the alt text so a blocked image still reads correctly.
 */
export function Flag({ code, className = 'flag' }) {
  if (!code || !/^[A-Z]{2}$/.test(code)) return null;
  const lower = code.toLowerCase();
  return (
    <img
      className={className}
      src={`https://flagcdn.com/w40/${lower}.png`}
      srcSet={`https://flagcdn.com/w80/${lower}.png 2x`}
      width={20}
      height={15}
      alt={flagOf(code)}
      title={countryName(code)}
      loading="lazy"
      decoding="async"
    />
  );
}

/** Only admins and super admins may open a customer's account — the role, not the permission. */
export const canOpenAccounts = (me) => me?.role === 'admin' || me?.role === 'super_admin';

/**
 * "Open account": a one-time magic link into the customer's own dashboard.
 *
 * The new tab is opened before the request, not after — browsers only allow
 * `window.open` inside the click itself, and an `await` in between is enough
 * for a popup blocker to eat it.
 */
export const openCustomerAccount = async (user) => {
  const reason = window.prompt(
    `Open ${user.email}'s account?\n\nGive a reason — it goes in the audit log. The link is single-use and valid for 10 minutes.`,
    '',
  );
  if (reason == null) return;
  if (reason.trim().length < 3) { window.alert('A reason of at least a few words is required.'); return; }
  const tab = window.open('', '_blank');
  try {
    const result = await api(`/users/${user.id}/support-sessions`, { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) });
    if (tab) tab.location = result.url; else window.location.assign(result.url);
  } catch (caught) {
    tab?.close();
    window.alert(caught.message);
  }
};

/** Buckets in the order the charts show them; the API returns them unordered. */
const ENGAGEMENT_BUCKETS = [['0', 'No videos', 'Never finished a video'], ['1', '1 video'], ['2-5', '2–5 videos'], ['6-20', '6–20 videos'], ['21+', '21+ videos']];
const ACTIVITY_BUCKETS = [['7d', 'This week', 'Active in the last 7 days'], ['30d', '8–30 days', 'Last active 8–30 days ago'], ['90d', '31–90 days', 'Last active 31–90 days ago'], ['older', '90+ days', 'Last active more than 90 days ago'], ['never', 'Never', 'Never used the product']];
const PROVIDER_LABELS = { local: 'Email & password', google: 'Google' };
/** Countries drawn as their own slice; the long tail is one neutral "Other" slice. */
const COUNTRY_SLICES = SERIES_COLORS.length;

const bucketRows = (rows, buckets) => {
  const byKey = Object.fromEntries((rows ?? []).map((r) => [r.bucket, r.users]));
  return buckets.map(([key, label, title]) => ({ key, label, title, value: byKey[key] ?? 0 }));
};

/**
 * The charts, drawn only once an operator asks for them. They sit in their
 * own grid above the table, never beside it, so opening them changes nothing
 * about the table's width or columns.
 */
function CustomerCharts() {
  const [data, setData] = useCachedState('/users/analytics');
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const refreshVersion = useRefreshVersion();

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api('/users/analytics').then((next) => { if (!cancelled) setData(next); })
      .catch((caught) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshVersion, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const countrySlices = useMemo(() => {
    const rows = data?.countries ?? [];
    const top = rows.slice(0, COUNTRY_SLICES).map((c, i) => ({ key: c.country, title: countryName(c.country), label: <><Flag code={c.country} />{countryName(c.country)}</>, value: c.users, color: SERIES_COLORS[i] }));
    const rest = rows.slice(COUNTRY_SLICES).reduce((sum, c) => sum + c.users, 0);
    if (rest > 0) top.push({ key: 'other', title: `Other (${rows.length - COUNTRY_SLICES} countries)`, label: `Other · ${rows.length - COUNTRY_SLICES} countries`, value: rest, color: OTHER_COLOR });
    return top;
  }, [data]);
  const providerRows = useMemo(() => (data?.providers ?? []).map((p) => ({ key: p.provider, label: PROVIDER_LABELS[p.provider] ?? p.provider, value: p.users })), [data]);
  const engagementRows = useMemo(() => bucketRows(data?.engagement, ENGAGEMENT_BUCKETS), [data]);
  const activityRows = useMemo(() => bucketRows(data?.activity, ACTIVITY_BUCKETS), [data]);

  if (error) return <ErrorState message={error} retry={() => setAttempt((n) => n + 1)} />;
  return <section className={`chart-grid customer-charts ${loading ? 'is-loading' : ''}`}>
    <article className="panel"><div className="panel-heading"><div><span className="eyebrow">GROWTH</span><h2>New customers per day</h2></div><small className="plain">Last {data?.signupDays ?? 30} days</small></div>
      {data?.signups?.length ? <SignupsByDay signups={data.signups} /> : loading ? <div className="chart-placeholder" /> : <Empty title="No sign-ups yet" body="New accounts will appear here as they join." />}
    </article>
    <article className="panel"><div className="panel-heading"><div><span className="eyebrow">COUNTRIES</span><h2>Where customers are</h2></div></div>
      {countrySlices.length || loading ? <ShareDonut slices={countrySlices} /> : <Empty title="No countries recorded" body="Country is captured at sign-up." />}
    </article>
    <article className="panel"><div className="panel-heading"><div><span className="eyebrow">ENGAGEMENT</span><h2>Videos finished per customer</h2></div></div>
      <BucketBars rows={engagementRows} />
    </article>
    <article className="panel"><div className="panel-heading"><div><span className="eyebrow">RECENCY</span><h2>Last activity</h2></div></div>
      <BucketBars rows={activityRows} color="#1baf7a" />
    </article>
    <article className="panel"><div className="panel-heading"><div><span className="eyebrow">SIGN-IN</span><h2>How customers sign in</h2></div></div>
      <BucketBars rows={providerRows} color="#eb6834" />
    </article>
  </section>;
}

const Stars = ({ value }) => value == null ? null : <span className="stars" title={`${value} / 5`}>{'★'.repeat(Math.round(value))}<i>{'★'.repeat(5 - Math.round(value))}</i></span>;

export function Users() {
  const me = useMe();
  const [filters, setFilters] = useState({ search: '', segment: '', country: '', plan: '', sort: 'active' });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(0);
  const [showCharts, setShowCharts] = useState(false);
  const defaultQuery = new URLSearchParams({ limit: PAGE_SIZE, offset: 0, sort: 'active' }).toString();
  const [data, setData] = useCachedState(`/users?${defaultQuery}`);
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState('');
  const refreshVersion = useRefreshVersion();

  // Typing filters live, a beat after the last keystroke, so a half-typed
  // email does not fire a query per key.
  useEffect(() => {
    const timer = setTimeout(() => { setApplied(filters); setPage(0); }, filters.search === applied.search ? 0 : 350);
    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE, sort: applied.sort };
    if (applied.search.trim()) params.search = applied.search.trim();
    if (applied.segment) params.segment = applied.segment;
    if (applied.country) params.country = applied.country;
    if (applied.plan) params.plan = applied.plan;
    return new URLSearchParams(params).toString();
  }, [applied, page]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api(`/users?${query}`).then((next) => { if (!cancelled) setData(next); })
      .catch((caught) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, refreshVersion]);


  const set = (key) => (event) => setFilters((f) => ({ ...f, [key]: event.target.value }));
  const total = data?.page?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const s = data?.summary;
  const activeCount = ['search', 'segment', 'country', 'plan'].filter((k) => applied[k]).length;

  return <>
    <section className="page-title"><div><div className="eyebrow">Customers</div><h1>Customers</h1><p>Every account, where it came from, and how much it uses the product.</p></div>
      <div className="page-actions">
        {activeCount > 0 && <button className="subtle" onClick={() => setFilters({ search: '', segment: '', country: '', plan: '', sort: filters.sort })}>Clear {activeCount} filter{activeCount > 1 ? 's' : ''}</button>}
        <button className={showCharts ? 'primary' : 'subtle'} aria-pressed={showCharts} onClick={() => setShowCharts((v) => !v)}>{showCharts ? 'Hide charts' : 'Show charts'}</button>
      </div></section>

    <section className={`metric-grid ${loading && !data ? 'is-loading' : ''}`}>
      <Metric label="Customers" value={shortNumber(s?.total)} detail={`${s?.new_users ?? 0} joined in the last ${data?.newUserDays ?? 7} days`} />
      <Metric label="Active this week" value={shortNumber(s?.active_7d)} detail="Transcribed or exported in 7 days" />
      <Metric label="Countries" value={shortNumber(s?.countries)} detail={data?.countries?.length ? <span className="country-chips">{data.countries.slice(0, 3).map((c) => <span key={c.country}><Flag code={c.country} /> {c.country} {c.users}</span>)}</span> : 'Recorded at sign-up'} />
      <Metric label="Subscribers" value={shortNumber(s?.subscribers)} detail={s?.total ? `${((Number(s.subscribers ?? 0) / Number(s.total)) * 100).toFixed(1)}% of customers on a paid plan` : 'On a paid plan right now'} />
      <Metric label="Used voiceovers" value={shortNumber(s?.with_voiceovers)} detail={s?.total ? `${((Number(s.with_voiceovers ?? 0) / Number(s.total)) * 100).toFixed(1)}% of customers made an AI voiceover` : 'Made at least one AI voiceover'} />
      <Metric label="Have rendered" value={shortNumber(s?.with_renders)} detail={s?.total ? `${Math.round((Number(s.with_renders) / Number(s.total)) * 100)}% of all customers finished a video` : 'Finished at least one video'} />
    </section>

    {showCharts && <CustomerCharts />}

    <section className="toolbar">
      <input value={filters.search} onChange={set('search')} placeholder="Search name, email, or user ID" />
      <Select value={filters.segment} onChange={set('segment')} label="Segment" options={[['', 'New and old'], ['new', `New (first ${data?.newUserDays ?? 7} days)`], ['old', 'Old (established)']]} />
      <Select value={filters.country} onChange={set('country')} label="Country" options={[['', 'All countries'], ...(data?.countries ?? []).map((c) => [c.country, `${flagOf(c.country)} ${countryName(c.country)} (${c.users})`])]} />
      <Select value={filters.plan} onChange={set('plan')} label="Plan" options={PLAN_FILTERS} />
      <Select value={filters.sort} onChange={set('sort')} label="Sort" options={SORTS} />
    </section>

    {error && <ErrorState message={error} retry={() => setApplied({ ...applied })} />}

    <article className={`panel table-panel ${loading ? 'is-loading' : ''}`}>
        <div className="panel-heading"><div><span className="eyebrow">ACCOUNTS</span><h2>{total} customer{total === 1 ? '' : 's'}</h2></div>
          <div className="pager"><button className="subtle" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button><span>Page {page + 1} of {pages}</span><button className="subtle" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button></div></div>
        <div className="table-wrap"><table><thead><tr><th>Customer</th><th>Plan</th><th>Joined</th><th>Activity</th><th>Videos</th><th>Voiceovers</th><th>Downloads</th><th>Credits</th><th>Rating</th>{canOpenAccounts(me) && <th />}</tr></thead><tbody>
          {data?.items?.length ? data.items.map((u) => <tr key={u.id}>
            <td><b>{u.full_name || u.email}</b><small title={u.id}>{u.full_name ? u.email : u.id}</small>
              <small className="plain country-line">{u.country ? <><Flag code={u.country} />{countryName(u.country)}</> : <span className="unassigned">Country unknown</span>}</small></td>
            <td><PlanCell u={u} /></td>
            <td><Badge tone={u.is_new ? 'info' : 'muted'}>{u.is_new ? 'New' : 'Old'}</Badge><small>{date(u.created_at)}</small></td>
            <td><b>{u.activity_30d}</b><small>{u.last_active_at ? `last ${ago(u.last_active_at)}` : 'never active'}</small></td>
            <td><b>{u.renders_done}</b><small>{u.projects} project{Number(u.projects) === 1 ? '' : 's'}</small></td>
            <td>{Number(u.voiceovers) || Number(u.script_writes) ? <><b>{voiceMinutes(u.voice_seconds)}</b><small>{u.voiceovers} clip{Number(u.voiceovers) === 1 ? '' : 's'} · {u.script_writes} script{Number(u.script_writes) === 1 ? '' : 's'}{Number(u.voice_credits) > 0 ? ` · ${u.voice_credits} cr` : ''}</small></> : <span className="unassigned">—</span>}</td>
            <td><b>{u.downloads}</b></td>
            <td><b>{shortNumber(u.credit_balance)}</b></td>
            <td>{u.avg_rating ? <><Stars value={Number(u.avg_rating)} /><small className="plain">{u.feedback_count} answer{Number(u.feedback_count) === 1 ? '' : 's'}</small></> : <span className="unassigned">—</span>}</td>
            {canOpenAccounts(me) && <td className="row-actions">{u.is_team ? <Badge>Team</Badge> : <button className="subtle" onClick={() => openCustomerAccount(u)}>Open account ↗</button>}</td>}
          </tr>) : <tr><td colSpan="11"><Empty title="No customers match" body="Try clearing a filter." /></td></tr>}
        </tbody></table></div>
    </article>
  </>;
}
